import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  highlightTextNodes,
  removeNodeHighlights,
} from "../common/utils/addOnServices/highlightTextNodes";
import {
  resolveSignedImageSources,
  useFailedImagePlaceholders,
} from "../common/services/microsoftImages";

interface ResponseLike {
  text(): Promise<string>;
  json(): Promise<any>;
}

interface MessageSender {
  user: {
    displayName: string;
    id: string;
  };
}

interface MessageBody {
  content: string;
  contentType: string;
}

interface Message {
  id: string;
  replyToId?: string;
  createdDateTime: string;
  lastModifiedDateTime?: string;
  deletedDateTime?: string;
  from: MessageSender;
  body: MessageBody;
  attachments?: any[];
  mentions?: any[];
  reactions?: any[];
}

interface Channel {
  id: string;
  displayName: string;
}

interface TeamsPost {
  team_id: string;
  ms_channel_id: string;
  channel: Channel;
  ms_message_id: string;
  message: Message;
  replies: { [key: string]: Message };
}

const TeamsPostViewer = ({
  response,
}: {
  response: ResponseLike | undefined;
}) => {
  const [post, setPost] = useState<TeamsPost | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const fileViewerData = useSelector((state: any) => state.fileViewerData);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (response) {
      setLoading(true);
    }

    const loadPost = async () => {
      if (!response) {
        return;
      }

      try {
        const jsonData = await response.json();
        setPost(jsonData);
        setError(null);
      } catch (err) {
        console.error("Failed to parse Teams post:", err);
        setError("Failed to parse Teams post");
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [response]);

  // Each image loads by itself from the signed URL the preview wrote, and one
  // that fails becomes a placeholder; the post never waits for its images.
  useFailedImagePlaceholders(
    contentRef,
    [
      post?.message?.body?.content,
      ...Object.values(post?.replies || {}).map((reply) => reply.body?.content),
    ].join("\n")
  );

  useEffect(() => {
    if (post && !loading) {
      highlightTextNodes("#teams-post-content", fileViewerData);
    }
  }, [fileViewerData, post, loading]);

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!loading && (error || !post)) {
    return (
      <div className="p-6 text-red-500">
        Error: {error || "Failed to load post"}
      </div>
    );
  }

  // Ensure post is not null before proceeding
  if (!post) {
    return <div className="p-6">No post data available</div>;
  }

  // Format date to readable format
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Convert replies object to array for easier mapping, filtering out deleted replies
  const repliesArray = Object.values(post.replies || {}).filter(
    (reply) => !reply.deletedDateTime
  );

  const highlightUrls = (content: string) => {
    const anchorRegex = /<a([^>]*?)>/gi;
    return content.replace(
      anchorRegex,
      '<a$1 style="color: #1976d2; font-weight: 500; text-decoration: none;">'
    );
  };

  const highlightMentions = (content: string, mentions: any[] = []) => {
    if (!mentions || mentions.length === 0) {
      return content;
    }
    let highlightedContent = content;
    mentions.forEach((mention) => {
      const mentionText = mention.mentionText;
      const displayName = mention.mentioned?.user?.displayName || mentionText;
      const escapedMentionText = mentionText.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&"
      );
      const regex = new RegExp(escapedMentionText, "gi");
      highlightedContent = highlightedContent.replace(
        regex,
        `<span style="color: #1976d2; font-weight: 500;">${displayName}</span>`
      );
    });
    return highlightedContent;
  };

  return (
    <div
      ref={contentRef}
      className="p-6 max-w-4xl"
      id="teams-post-content"
      onClick={() => {
        removeNodeHighlights("#teams-post-content");
      }}
    >
      {/* Channel info */}
      <div className="bg-gray-100 p-3 rounded-t-lg mb-4">
        <h2 className="font-bold text-lg">
          Channel: {post.channel.displayName}
        </h2>
      </div>

      {/* Main message */}
      <div className="border rounded-lg p-4 mb-6 shadow-sm">
        <div className="flex justify-between mb-2">
          <div className="font-semibold">
            {post.message.from.user.displayName}
          </div>
          <div className="text-sm text-gray-500">
            {formatDate(post.message.createdDateTime)}
          </div>
        </div>
        <div
          className="max-w-none"
          dangerouslySetInnerHTML={{
            __html: highlightUrls(
              highlightMentions(
                resolveSignedImageSources(post.message.body.content),
                post.message.mentions
              )
            ),
          }}
        />
      </div>

      {/* Replies */}
      {repliesArray.length > 0 && (
        <div className="ml-8">
          <h3 className="font-medium text-gray-700 mb-2">Replies:</h3>
          {repliesArray.map((reply, index) => (
            <div
              key={reply.id}
              className="border rounded-lg p-4 mb-3 shadow-sm"
            >
              <div className="flex justify-between mb-2">
                <div className="font-semibold">
                  {reply.from.user.displayName}
                </div>
                <div className="text-sm text-gray-500">
                  {formatDate(reply.createdDateTime)}
                </div>
              </div>
              <div
                className="max-w-none"
                dangerouslySetInnerHTML={{
                  __html: highlightUrls(
                    highlightMentions(
                      resolveSignedImageSources(reply.body.content),
                      reply.mentions
                    )
                  ),
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamsPostViewer;
